// src/components/PhotoPlaceImport.jsx
// Import places from photos using EXIF GPS data
// Groups photos by selected place so multiple photos → one place entry
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import EXIF from "exif-js";
import { API_BASE } from "../context/UserContext";
import { mapGoogleCategory } from "../constants";
import SavePlaceModal from "./SavePlaceModal";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54",
  primaryDim: "#595149",
  bg: "#faf9f6",
  container: "#f4f4f0",
  onSurface: "#2f3430",
  outline: "#8a8e8a",
};

/* ── EXIF data extraction (GPS + DateTime) ── */
function readExifData(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buf = e.target.result;
        const tags = EXIF.readFromBinaryFile(buf);
        if (!tags) { resolve({ gps: null, datetime: null }); return; }

        // GPS
        let gps = null;
        if (tags.GPSLatitude && tags.GPSLongitude) {
          const lat = dmsToDecimal(tags.GPSLatitude, tags.GPSLatitudeRef);
          const lng = dmsToDecimal(tags.GPSLongitude, tags.GPSLongitudeRef);
          if (!isNaN(lat) && !isNaN(lng)) gps = { lat, lng };
        }

        // DateTime — try DateTimeOriginal first, fallback to DateTime
        let datetime = null;
        const dtStr = tags.DateTimeOriginal || tags.DateTime;
        if (dtStr) {
          // EXIF format: "2024:03:15 14:30:00"
          const [datePart, timePart] = dtStr.split(" ");
          if (datePart) {
            const isoStr = datePart.replace(/:/g, "-") + (timePart ? `T${timePart}` : "T00:00:00");
            const d = new Date(isoStr);
            if (!isNaN(d.getTime())) datetime = d;
          }
        }

        resolve({ gps, datetime });
      } catch {
        resolve({ gps: null, datetime: null });
      }
    };
    reader.onerror = () => resolve({ gps: null, datetime: null });
    reader.readAsArrayBuffer(file);
  });
}

function dmsToDecimal(dms, ref) {
  const d = dms[0] + dms[1] / 60 + dms[2] / 3600;
  return (ref === "S" || ref === "W") ? -d : d;
}

/* ── Haversine distance (meters) ── */
function haversineM(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ── Google Places nearby search ── */
async function searchNearbyPlaces(lat, lng) {
  try {
    const { Place } = await window.google.maps.importLibrary("places");
    const center = new window.google.maps.LatLng(lat, lng);
    const request = {
      fields: ["displayName", "formattedAddress", "location", "id", "types"],
      locationRestriction: { center, radius: 50 },
      maxResultCount: 5,
    };
    const { places } = await Place.searchNearby(request);
    return (places || []).map((p) => ({
      name: p.displayName,
      address: p.formattedAddress,
      lat: p.location.lat(),
      lng: p.location.lng(),
      google_place_id: p.id,
      category: mapGoogleCategory(p.types),
    }));
  } catch {
    return [];
  }
}

/* ── Component ── */
export default function PhotoPlaceImport({ onPlaceAdded, onClose, onViewTrip, editTrip, extraSection, savedPlaces = [] }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [step, setStep] = useState(editTrip ? "results" : "select"); // select | processing | results
  // Each photo: { file, blobUrl, gps, candidates, selectedPlaceId, status }
  const [photoEntries, setPhotoEntries] = useState(() => {
    if (!editTrip) return [];
    // Convert existing trip stops into photoEntries format
    const entries = [];
    for (const stop of (editTrip.stops || [])) {
      const photos = stop.photo_urls || [];
      if (photos.length === 0 && stop.photo_url) photos.push(stop.photo_url);
      const placeCandidate = {
        name: stop.name || "",
        address: stop.address || "",
        lat: stop.lat,
        lng: stop.lng,
        google_place_id: stop.google_place_id || `existing_${stop.lat}_${stop.lng}`,
      };
      for (const url of (photos.length > 0 ? photos : [""])) {
        entries.push({
          file: null,
          blobUrl: url || null, // server URL used as blobUrl for display
          gps: stop.lat != null ? { lat: stop.lat, lng: stop.lng } : null,
          datetime: (() => {
            if (!stop.visited_at) return null;
            const d = new Date(`2000-01-01T00:00:00`);
            // Parse "10:30 AM" style time
            const m = stop.visited_at.match(/(\d+):(\d+)\s*(AM|PM)?/i);
            if (m) {
              let h = parseInt(m[1]);
              const min = parseInt(m[2]);
              if (m[3]?.toUpperCase() === "PM" && h < 12) h += 12;
              if (m[3]?.toUpperCase() === "AM" && h === 12) h = 0;
              d.setHours(h, min, 0, 0);
              return d;
            }
            return null;
          })(),
          candidates: [placeCandidate],
          selectedPlaceId: placeCandidate.google_place_id,
          status: "ready",
          isExisting: true, // flag to skip upload later
        });
      }
    }
    return entries;
  });
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingSave, setPendingSave] = useState(null);
  const [savedPlaceIds, setSavedPlaceIds] = useState(new Set());
  // Google Maps list import for better matching
  const [gmapUrl, setGmapUrl] = useState("");
  const [gmapParsing, setGmapParsing] = useState(false);
  const [gmapPlaces, setGmapPlaces] = useState([]); // parsed places from Google Maps list
  const [gmapError, setGmapError] = useState("");
  const [tripNames, setTripNames] = useState(() => {
    if (!editTrip) return {};
    return { [editTrip.date || "_nodate"]: editTrip.name || "" };
  });
  const [savedPlaceDbIds, setSavedPlaceDbIds] = useState({}); // { groupKey: backendPlaceId }

  // Lock body scroll when modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Find saved places near a GPS coordinate (distance < 0.001 ≈ ~111m)
  const findNearbySavedPlaces = useCallback((lat, lng) => {
    const THRESHOLD = 0.001;
    return savedPlaces
      .filter((p) => p.lat != null && p.lng != null)
      .map((p) => ({
        dist: Math.sqrt((p.lat - lat) ** 2 + (p.lng - lng) ** 2),
        place: p,
      }))
      .filter((x) => x.dist < THRESHOLD)
      .sort((a, b) => a.dist - b.dist)
      .map((x) => ({
        name: x.place.name,
        address: x.place.address || "",
        lat: x.place.lat,
        lng: x.place.lng,
        google_place_id: x.place.google_place_id || `saved_${x.place.id}`,
        category: x.place.category || null,
      }));
  }, [savedPlaces]);

  const handleFilesSelected = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setStep("processing");
    setTotalCount(files.length);
    setProcessedCount(0);

    const entries = [];

    for (const file of files) {
      const blobUrl = URL.createObjectURL(file);
      const { gps, datetime } = await readExifData(file);

      if (!gps) {
        entries.push({ file, blobUrl, gps: null, datetime, candidates: [], selectedPlaceId: null, status: "no_gps" });
      } else {
        // Check saved places first, then Google Places API
        const nearbyOwn = findNearbySavedPlaces(gps.lat, gps.lng);
        const googleCandidates = await searchNearbyPlaces(gps.lat, gps.lng);
        // Merge: enrich saved places with Google API data, then append remaining Google results
        const isMatch = (a, b) => {
          if (a.google_place_id && b.google_place_id && a.google_place_id === b.google_place_id) return true;
          if (a.name && b.name && a.name.toLowerCase() === b.name.toLowerCase()
              && Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2) < 0.002) return true;
          return false;
        };
        const usedGoogle = new Set();
        const enriched = nearbyOwn.map((own) => {
          const gMatch = googleCandidates.find((g) => isMatch(own, g));
          if (gMatch) {
            usedGoogle.add(gMatch.google_place_id);
            return { ...own, google_place_id: gMatch.google_place_id, address: gMatch.address || own.address };
          }
          return own;
        });
        const merged = [...enriched, ...googleCandidates.filter((c) => !usedGoogle.has(c.google_place_id) && !enriched.some((e) => isMatch(e, c)))];
        entries.push({
          file, blobUrl, gps, datetime, candidates: merged,
          selectedPlaceId: merged[0]?.google_place_id || null,
          status: merged.length > 0 ? "ready" : "no_match",
        });
      }
      setProcessedCount((c) => c + 1);
    }

    setPhotoEntries((prev) => [...prev, ...entries]);
    setStep("results");
    if (fileRef.current) fileRef.current.value = "";
  }, [findNearbySavedPlaces]);

  // Group photos by selected place (or by GPS coords if no match)
  // Get a place key for an entry (used for consecutive grouping)
  const getEntryPlaceKey = useCallback((entry) => {
    if (entry.status === "no_gps" && !entry.datetime) return null; // truly no info
    if (entry.status === "ready" && entry.selectedPlaceId) return entry.selectedPlaceId;
    if (entry.status === "no_match" && entry.gps) return `gps_${entry.gps.lat.toFixed(4)}_${entry.gps.lng.toFixed(4)}`;
    if (entry.status === "no_gps" && entry.datetime) return `nogps_${entry.datetime.getTime()}`;
    return null;
  }, []);

  // Group photos CONSECUTIVELY by place — same place separated by another place = separate groups
  const placeGroups = useMemo(() => {
    // Sort entries by datetime (entries without datetime go last)
    const indexed = photoEntries.map((entry, idx) => ({ entry, idx }));
    indexed.sort((a, b) => {
      const ta = a.entry.datetime?.getTime() ?? Infinity;
      const tb = b.entry.datetime?.getTime() ?? Infinity;
      return ta - tb;
    });

    const groups = {};
    let lastPlaceKey = null;
    let lastGroupKey = null;
    let lastGps = null; // track last GPS-only group's coordinates
    let seqNum = 0;

    indexed.forEach(({ entry, idx }) => {
      const baseKey = getEntryPlaceKey(entry);
      if (!baseKey) return; // no info → skip

      const isGpsOnly = entry.status === "no_match" && entry.gps;
      const lastIsGpsOnly = lastPlaceKey?.startsWith("gps_");

      let mergeWithLast = false;

      if (baseKey !== lastPlaceKey) {
        // Check if both current and last are GPS-only and within 500m
        if (isGpsOnly && lastIsGpsOnly && lastGps) {
          const dist = haversineM(lastGps.lat, lastGps.lng, entry.gps.lat, entry.gps.lng);
          if (dist < 500) {
            mergeWithLast = true;
          }
        }

        if (!mergeWithLast) {
          seqNum++;
          lastPlaceKey = baseKey;
          if (isGpsOnly) lastGps = { lat: entry.gps.lat, lng: entry.gps.lng };
          else lastGps = null;
        }
      }

      const groupKey = mergeWithLast ? lastGroupKey : `${baseKey}__${seqNum}`;
      lastGroupKey = groupKey;

      if (!groups[groupKey]) {
        let place, type;
        if (entry.status === "ready" && entry.selectedPlaceId) {
          const candidate = entry.candidates.find((c) => c.google_place_id === entry.selectedPlaceId);
          place = candidate;
          type = "place";
        } else if (isGpsOnly) {
          place = { name: "", address: "", lat: entry.gps.lat, lng: entry.gps.lng };
          type = "gps_only";
        } else if (entry.status === "no_gps" && entry.datetime) {
          place = { name: "", address: "", lat: null, lng: null };
          type = "no_location";
        }
        groups[groupKey] = { place, photoIndices: [], photos: [], type, baseKey };
      }

      groups[groupKey].photoIndices.push(idx);
      groups[groupKey].photos.push({ file: entry.file, blobUrl: entry.blobUrl, datetime: entry.datetime, isExisting: entry.isExisting || false });
    });

    return groups;
  }, [photoEntries, getEntryPlaceKey]);

  // Combine all place groups into one trip (sorted by time), no-date stops at the end
  const trips = useMemo(() => {
    const withTime = [];
    const noDateKeys = [];

    Object.entries(placeGroups).forEach(([key, group]) => {
      const times = group.photos.map((p) => p.datetime).filter(Boolean);
      const earliest = times.length > 0 ? new Date(Math.min(...times.map((d) => d.getTime()))) : null;
      group._earliestTime = earliest;

      if (earliest) {
        withTime.push(key);
      } else {
        noDateKeys.push(key);
      }
    });

    // Sort by time
    withTime.sort((a, b) => {
      const ta = placeGroups[a]._earliestTime?.getTime() || 0;
      const tb = placeGroups[b]._earliestTime?.getTime() || 0;
      return ta - tb;
    });

    const allKeys = [...withTime, ...noDateKeys];
    if (allKeys.length === 0) return [];

    // Find date range for display
    const firstDate = withTime.length > 0 ? placeGroups[withTime[0]]._earliestTime : null;
    const lastDate = withTime.length > 0 ? placeGroups[withTime[withTime.length - 1]]._earliestTime : null;
    const dateKey = firstDate ? firstDate.toISOString().slice(0, 10) : "_nodate";

    return [{
      dateKey,
      date: firstDate,
      endDate: lastDate,
      placeKeys: allKeys,
    }];
  }, [placeGroups]);

  const handleRemoveGroup = useCallback((photoIndices) => {
    setPhotoEntries((prev) => prev.filter((_, i) => !photoIndices.includes(i)));
  }, []);

  const handleRemovePhoto = useCallback((photoIdx) => {
    setPhotoEntries((prev) => prev.filter((_, i) => i !== photoIdx));
  }, []);

  const handleAddPhotosToGroup = useCallback(async (group, files) => {
    if (!files.length) return;
    const refEntry = photoEntries[group.photoIndices[0]];
    const newEntries = [];
    for (const file of files) {
      const blobUrl = URL.createObjectURL(file);
      const { datetime } = await readExifData(file);
      newEntries.push({
        file, blobUrl,
        gps: refEntry?.gps || null,
        datetime: datetime || refEntry?.datetime || null,
        candidates: refEntry?.candidates || [],
        selectedPlaceId: refEntry?.selectedPlaceId || null,
        status: refEntry?.status || "no_match",
      });
    }
    // Insert right after the last photo in this group
    const insertAfter = Math.max(...group.photoIndices);
    setPhotoEntries((prev) => [
      ...prev.slice(0, insertAfter + 1),
      ...newEntries,
      ...prev.slice(insertAfter + 1),
    ]);
  }, [photoEntries]);

  const handleSelectCandidate = (photoIdx, placeId) => {
    setPhotoEntries((prev) => prev.map((entry, i) => {
      if (i !== photoIdx) return entry;
      // If placeId is null → coordinates-only mode
      if (placeId === null) {
        return { ...entry, selectedPlaceId: null, status: "no_match" };
      }
      return { ...entry, selectedPlaceId: placeId, status: "ready" };
    }));
  };

  const handleOverridePlace = useCallback((photoIndices, newPlace) => {
    setPhotoEntries((prev) => prev.map((entry, i) => {
      if (!photoIndices.includes(i)) return entry;
      const alreadyHas = entry.candidates.some((c) => c.google_place_id === newPlace.google_place_id);
      return {
        ...entry,
        candidates: alreadyHas ? entry.candidates : [...entry.candidates, newPlace],
        selectedPlaceId: newPlace.google_place_id,
        status: "ready",
        // Set GPS from the place if the photo had no GPS
        gps: entry.gps || (newPlace.lat && newPlace.lng ? { lat: newPlace.lat, lng: newPlace.lng } : entry.gps),
      };
    }));
  }, []);

  const handleSaveGroup = (groupKey) => {
    const group = placeGroups[groupKey];
    if (!group) return;
    setPendingSave({
      ...group.place,
      _initPhotos: group.photos.map((p) => ({
        type: "new", file: p.file, blobUrl: p.blobUrl,
      })),
      _baseKey: group.baseKey,
    });
  };

  const handleSaveComplete = (savedPlace) => {
    if (!savedPlace) return;
    const baseKey = pendingSave?._baseKey;
    setSavedPlaceIds((prev) => new Set([...prev, baseKey]));
    if (savedPlace.id) {
      setSavedPlaceDbIds((prev) => ({ ...prev, [baseKey]: savedPlace.id }));
    }
    onPlaceAdded?.(savedPlace);
    setPendingSave(null);
  };

  // Parse Google Maps list and re-match with existing photo entries
  const handleGmapImport = async () => {
    const trimmed = gmapUrl.trim();
    if (!trimmed || (!trimmed.includes("google") && !trimmed.includes("goo.gl") && !trimmed.includes("maps"))) {
      setGmapError(t("import.invalid_url"));
      return;
    }
    setGmapError("");
    setGmapParsing(true);
    try {
      const resp = await axios.post(`${API_BASE}/google-import/parse-list`, { url: trimmed });
      const places = resp.data?.places || [];
      if (places.length === 0) {
        setGmapError(t("import.no_places"));
        setGmapParsing(false);
        return;
      }
      setGmapPlaces(places);
      // Re-match: for each photo with GPS, check if any imported place is nearby
      const THRESHOLD = 0.001;
      setPhotoEntries((prev) => prev.map((entry) => {
        if (!entry.gps) return entry;
        const nearby = places
          .filter((p) => p.lat != null && p.lng != null)
          .map((p) => ({
            dist: Math.sqrt((p.lat - entry.gps.lat) ** 2 + (p.lng - entry.gps.lng) ** 2),
            place: p,
          }))
          .filter((x) => x.dist < THRESHOLD)
          .sort((a, b) => a.dist - b.dist)
          .map((x) => ({
            name: x.place.name,
            address: x.place.address || "",
            lat: x.place.lat,
            lng: x.place.lng,
            google_place_id: x.place.google_place_id || `gmap_${x.place.lat}_${x.place.lng}`,
            category: x.place.category || null,
          }));
        if (nearby.length === 0) return entry;
        // Enrich existing candidates with Google data, then prepend new matches
        const isMatch = (a, b) => {
          if (a.google_place_id && b.google_place_id && a.google_place_id === b.google_place_id) return true;
          if (a.name && b.name && a.name.toLowerCase() === b.name.toLowerCase()
              && Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2) < 0.002) return true;
          return false;
        };
        const usedNearby = new Set();
        const enrichedExisting = entry.candidates.map((c) => {
          const nMatch = nearby.find((n) => isMatch(c, n));
          if (nMatch) {
            usedNearby.add(nMatch.google_place_id);
            return { ...c, google_place_id: nMatch.google_place_id || c.google_place_id, address: nMatch.address || c.address };
          }
          return c;
        });
        const newNearby = nearby.filter((n) => !usedNearby.has(n.google_place_id) && !enrichedExisting.some((e) => isMatch(e, n)));
        const merged = [...newNearby, ...enrichedExisting];
        return {
          ...entry,
          candidates: merged,
          selectedPlaceId: merged[0]?.google_place_id || entry.selectedPlaceId,
          status: "ready",
        };
      }));
    } catch {
      setGmapError(t("import.error"));
    } finally {
      setGmapParsing(false);
    }
  };

  // Find existing personal place by coordinate proximity
  const findExistingPlace = useCallback((lat, lng) => {
    const THRESHOLD = 0.001;
    for (const p of savedPlaces) {
      if (p.lat != null && p.lng != null) {
        const d = Math.sqrt((p.lat - lat) ** 2 + (p.lng - lng) ** 2);
        if (d < THRESHOLD) return p;
      }
    }
    return null;
  }, [savedPlaces]);

  const handleBulkSavePlaces = async (groups) => {
    for (const g of groups) {
      if (!g?.place || savedPlaceIds.has(g.baseKey)) continue;
      try {
        // Upload photos
        let newPhotoUrls = [];
        const photosToUpload = g.photos.filter((p) => p.file);
        if (photosToUpload.length > 0) {
          const formData = new FormData();
          for (const p of photosToUpload.slice(0, 5)) formData.append("files", p.file);
          const uploadRes = await axios.post(`${API_BASE}/upload/photos`, formData);
          newPhotoUrls = uploadRes.data.urls || [];
        }

        // Check if place already exists in personal places
        const existing = findExistingPlace(g.place.lat, g.place.lng);

        if (existing) {
          // Merge: append new photos to existing place
          const existingPhotos = existing.photo_urls || (existing.photo_url ? [existing.photo_url] : []);
          const mergedPhotos = [...existingPhotos, ...newPhotoUrls];
          await axios.patch(`${API_BASE}/personal-places/${existing.id}`, {
            photo_urls: mergedPhotos.length > 0 ? mergedPhotos : null,
            status: "visited",
          });
          setSavedPlaceIds((prev) => new Set([...prev, g.baseKey]));
          setSavedPlaceDbIds((prev) => ({ ...prev, [g.baseKey]: existing.id }));
          onPlaceAdded?.(existing);
        } else {
          // Create new place
          const res = await axios.post(`${API_BASE}/personal-places/`, {
            name: g.place.name || "Unknown",
            address: g.place.address || "",
            lat: g.place.lat,
            lng: g.place.lng,
            google_place_id: g.place.google_place_id || null,
            category: g.place.category || null,
            status: "visited",
            photo_urls: newPhotoUrls.length > 0 ? newPhotoUrls : null,
          });
          setSavedPlaceIds((prev) => new Set([...prev, g.baseKey]));
          if (res.data?.id) {
            setSavedPlaceDbIds((prev) => ({ ...prev, [g.baseKey]: res.data.id }));
          }
          onPlaceAdded?.(res.data);
        }
      } catch (err) {
        console.error("Bulk save place failed:", err);
      }
    }
  };

  const noGpsCount = photoEntries.filter((e) => e.status === "no_gps" && !e.datetime).length;
  const groupKeys = Object.keys(placeGroups);

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(47,52,48,0.4)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 100,
        overflow: "hidden",
      }}
    >
      <div style={{
        background: C.bg,
        borderRadius: "20px 20px 0 0",
        width: "100%", maxWidth: 520,
        maxHeight: "85vh",
        overflowY: "auto", overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-y",
        boxShadow: "0 -8px 40px rgba(47,52,48,0.12)",
      }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 6px" }}>
          <div style={{ width: 32, height: 3, background: C.outline, borderRadius: 2, opacity: 0.4 }} />
        </div>

        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 24px 16px",
        }}>
          <div>
            <p style={{ margin: 0, fontFamily: FL, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outline }}>
              {editTrip ? "Edit trip" : "Add from photos"}
            </p>
            <h3 style={{ margin: "4px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 20, fontWeight: 700, color: C.onSurface }}>
              {editTrip ? editTrip.name : "Photo Import"}
            </h3>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20, color: C.outline, cursor: "pointer", padding: 4,
          }}>✕</button>
        </div>

        <div style={{ padding: "0 24px 24px", minWidth: 0 }}>
          {/* Step: Select photos */}
          {step === "select" && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%",
                background: "#ede0d5",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 36, color: C.primary }}>
                  add_a_photo
                </span>
              </div>
              <p style={{ fontFamily: FL, fontSize: 15, fontWeight: 600, color: C.onSurface, marginBottom: 8 }}>
                Select photos with location data
              </p>
              <p style={{ fontFamily: FL, fontSize: 13, color: C.outline, marginBottom: 24, lineHeight: 1.6 }}>
                We'll find nearby places from your photos' GPS data<br />
                and let you save them to your map
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  padding: "14px 32px",
                  background: C.primary, color: "#fff6ef",
                  border: "none", borderRadius: 12,
                  fontFamily: FL, fontSize: 14, fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(101,93,84,0.2)",
                }}
              >
                Choose Photos
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                style={{ display: "none" }}
              />
            </div>
          )}

          {/* Step: Processing */}
          {step === "processing" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{
                width: 48, height: 48,
                border: `3px solid ${C.container}`,
                borderTopColor: C.primary,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 20px",
              }} />
              <p style={{ fontFamily: FL, fontSize: 15, fontWeight: 600, color: C.onSurface }}>
                Analyzing photos...
              </p>
              <p style={{ fontFamily: FL, fontSize: 13, color: C.outline, marginTop: 8 }}>
                {processedCount} / {totalCount}
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Step: Results */}
          {step === "results" && (
            <>
              {/* Trip title & date editor */}
              {trips.map((trip) => {
                const tn = tripNames[trip.dateKey] || "";
                const isNoDate = trip.dateKey === "_nodate";
                const dateRange = (() => {
                  if (isNoDate || !trip.date) return "No date info";
                  const start = formatDate(trip.date);
                  if (trip.endDate && trip.endDate.toISOString().slice(0, 10) !== trip.date.toISOString().slice(0, 10)) {
                    return `${start} — ${formatDate(trip.endDate)}`;
                  }
                  return start;
                })();
                return (
                  <div key={trip.dateKey} style={{
                    marginBottom: 16, padding: "16px 18px",
                    background: isNoDate ? "#f0f0ec" : "#FFF8F0",
                    borderRadius: 12,
                    border: `1px solid ${isNoDate ? "#e0e0dc" : "#F0DCC8"}`,
                  }}>
                    <input
                      value={tn}
                      onChange={(e) => setTripNames((prev) => ({ ...prev, [trip.dateKey]: e.target.value }))}
                      placeholder={isNoDate ? "Trip name" : dateRange}
                      style={{
                        width: "100%", padding: "8px 0",
                        background: "transparent", border: "none", borderBottom: `1.5px solid ${isNoDate ? "#d0d0cc" : "#F0DCC8"}`,
                        fontFamily: FL, fontSize: 18, fontWeight: 700,
                        color: C.onSurface, outline: "none", boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: isNoDate ? C.outline : "#E8943A" }}>
                        {isNoDate ? "help_outline" : "calendar_today"}
                      </span>
                      <span style={{ fontFamily: FL, fontSize: 13, color: C.outline }}>
                        {dateRange}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Summary */}
              <div style={{
                display: "flex", gap: 12, marginBottom: 16,
                padding: "12px 16px", background: C.container, borderRadius: 10,
              }}>
                <Stat label="Photos" value={photoEntries.length} color="#E8943A" />
                <Stat label="Stops" value={groupKeys.length} color="#655d54" />
                {savedPlaceIds.size > 0 && <Stat label="Saved" value={savedPlaceIds.size} color="#1D9E75" />}
                {noGpsCount > 0 && <Stat label="No GPS" value={noGpsCount} color={C.outline} />}
              </div>

              {/* Extra section (e.g. collaborators) */}
              {extraSection}

              {/* Google Maps list link for better matching */}
              <div style={{
                marginBottom: 16, padding: "12px 14px",
                background: "#f4f4f0", borderRadius: 10,
              }}>
                <p style={{ margin: "0 0 8px", fontFamily: FL, fontSize: 13, fontWeight: 700, color: "#655d54" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }}>link</span>
                  Google Maps
                </p>
                <p style={{ margin: "0 0 10px", fontFamily: FL, fontSize: 12, color: "#8a8e8a", lineHeight: 1.5 }}>
                  {t("import.gmap_hint")}
                </p>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    value={gmapUrl}
                    onChange={(e) => setGmapUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleGmapImport()}
                    placeholder="https://maps.app.goo.gl/..."
                    style={{
                      flex: 1, padding: "8px 10px",
                      background: "#fff", border: "1px solid #e8e8e4", borderRadius: 6,
                      fontFamily: FL, fontSize: 12, color: "#2f3430",
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <button
                    onClick={handleGmapImport}
                    disabled={gmapParsing || !gmapUrl.trim()}
                    style={{
                      padding: "8px 12px",
                      background: "#655d54", color: "#fff6ef",
                      border: "none", borderRadius: 6,
                      fontFamily: FL, fontSize: 11, fontWeight: 700,
                      cursor: gmapParsing ? "not-allowed" : "pointer",
                      opacity: gmapParsing || !gmapUrl.trim() ? 0.5 : 1,
                      flexShrink: 0,
                    }}
                  >
                    {gmapParsing ? "..." : t("import.parse")}
                  </button>
                </div>
                {gmapError && (
                  <p style={{ margin: "6px 0 0", fontFamily: FL, fontSize: 11, color: "#9e422c" }}>{gmapError}</p>
                )}
                {gmapPlaces.length > 0 && (
                  <p style={{ margin: "6px 0 0", fontFamily: FL, fontSize: 11, color: "#1D9E75", fontWeight: 600 }}>
                    ✓ {t("import.places_found", { count: gmapPlaces.length })}
                  </p>
                )}
              </div>

              {/* Trips */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {trips.map((trip) => (
                  <TripSection
                    key={trip.dateKey}
                    trip={trip}
                    tripName={tripNames[trip.dateKey] || ""}
                    onTripNameChange={(name) => setTripNames((prev) => ({ ...prev, [trip.dateKey]: name }))}
                    placeGroups={placeGroups}
                    savedPlaceIds={savedPlaceIds}
                    onSaveGroup={handleSaveGroup}
                    photoEntries={photoEntries}
                    onSelectCandidate={handleSelectCandidate}
                    onOverridePlace={handleOverridePlace}
                    onRemoveGroup={handleRemoveGroup}
                    onRemovePhoto={handleRemovePhoto}
                    onAddPhotos={handleAddPhotosToGroup}
                    onViewTrip={onViewTrip ? (tripData) => { onViewTrip(tripData); onClose(); } : null}
                    editTripId={editTrip?.id}
                    savedPlaceDbIds={savedPlaceDbIds}
                    onBulkSavePlaces={handleBulkSavePlaces}
                    existingPlaces={savedPlaces}
                  />
                ))}
              </div>

              {/* No-GPS photos */}
              {noGpsCount > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: "#f8f8f4", borderRadius: 10 }}>
                  <p style={{ margin: "0 0 8px", fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.outline }}>
                    No location data ({noGpsCount})
                  </p>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {photoEntries.filter((e) => e.status === "no_gps" && !e.datetime).map((e, i) => (
                      <img key={i} src={e.blobUrl} alt="" style={{
                        width: 40, height: 40, borderRadius: 6, objectFit: "cover", opacity: 0.5,
                      }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Add more */}
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  width: "100%", marginTop: 12,
                  padding: "12px",
                  border: `1.5px dashed ${C.outline}66`,
                  borderRadius: 10, background: "none",
                  fontFamily: FL, fontSize: 13, color: C.outline,
                  cursor: "pointer",
                }}
              >
                + Add more photos
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                style={{ display: "none" }}
              />
            </>
          )}
        </div>
      </div>

      {/* SavePlaceModal */}
      {pendingSave && (
        <SavePlaceModal
          place={pendingSave}
          onSave={async (placeData) => {
            handleSaveComplete(placeData);
          }}
          onClose={() => setPendingSave(null)}
        />
      )}
    </div>,
    document.body
  );
}

/* ── Sub-components ── */

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: 1, textAlign: "center" }}>
      <p style={{ margin: 0, fontFamily: FL, fontSize: 20, fontWeight: 700, color }}>{value}</p>
      <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outline }}>{label}</p>
    </div>
  );
}

function formatDate(date) {
  if (!date) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatTime(date) {
  if (!date) return "";
  const h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}

function TripSection({ trip, tripName, onTripNameChange, placeGroups, savedPlaceIds, onSaveGroup, photoEntries, onSelectCandidate, onOverridePlace, onRemoveGroup, onRemovePhoto, onAddPhotos, onViewTrip, savedPlaceDbIds = {}, editTripId, onBulkSavePlaces, existingPlaces = [] }) {
  const { t } = useTranslation();
  const isEdit = !!editTripId;
  // Checkbox state for "save to my places" — default all checked
  const [saveChecks, setSaveChecks] = useState(() => {
    const checks = {};
    for (const k of trip.placeKeys) {
      checks[k] = true;
    }
    return checks;
  });
  const [isPublic, setIsPublic] = useState(true);
  return (
    <div>

      {/* Timeline + place cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {trip.placeKeys.map((placeId, i) => {
          const group = placeGroups[placeId];
          const isSaved = savedPlaceIds.has(group.baseKey);
          const earliest = group._earliestTime;
          const isLast = i === trip.placeKeys.length - 1;

          return (
            <div key={placeId} style={{ display: "flex", gap: 0 }}>
              {/* Timeline rail */}
              {(
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  width: 48, flexShrink: 0,
                }}>
                  {/* Time label */}
                  <p style={{
                    margin: 0, fontFamily: FL, fontSize: 10, fontWeight: 600,
                    color: "#E8943A",
                    whiteSpace: "nowrap",
                  }}>
                    {earliest ? formatTime(earliest) : ""}
                  </p>
                  {/* Dot */}
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#E8943A",
                    margin: "4px 0 2px",
                    flexShrink: 0,
                  }} />
                  {/* Line */}
                  {!isLast && (
                    <div style={{
                      width: 2, flex: 1, minHeight: 12,
                      background: `linear-gradient(#E8943A, #E8943A44)`,
                    }} />
                  )}
                </div>
              )}

              {/* Card */}
              <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 8 }}>
                <PlaceGroupCard
                  group={group}
                  placeId={placeId}
                  isSaved={isSaved}
                  onSave={() => onSaveGroup(placeId)}
                  onRemove={() => onRemoveGroup(group.photoIndices)}
                  onRemovePhoto={onRemovePhoto}
                  onAddPhotos={onAddPhotos}
                  photoEntries={photoEntries}
                  onSelectCandidate={onSelectCandidate}
                  onOverridePlace={onOverridePlace}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Public/Private toggle */}
      {onViewTrip && trip.placeKeys.length > 0 && (
        <div style={{
          marginTop: 14, padding: "10px 14px",
          background: "#f4f4f0", borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: isPublic ? "#1D9E75" : "#8a8e8a" }}>
              {isPublic ? "public" : "lock"}
            </span>
            <span style={{ fontFamily: FL, fontSize: 13, fontWeight: 600, color: "#2f3430" }}>
              {isPublic ? t("profile.public_account") : t("profile.private_account")}
            </span>
          </div>
          <button
            onClick={() => setIsPublic(!isPublic)}
            style={{
              position: "relative", width: 40, height: 22,
              background: isPublic ? "#1D9E75" : "#c7c4bf",
              borderRadius: 11, border: "none", cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            <div style={{
              position: "absolute", top: 2,
              left: isPublic ? 20 : 2,
              width: 18, height: 18, borderRadius: "50%",
              background: "#fff",
              transition: "left 0.2s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
            }} />
          </button>
        </div>
      )}

      {/* Save to My Places checkboxes */}
      {onViewTrip && trip.placeKeys.length > 0 && !isEdit && (
        <div style={{
          marginTop: 14, padding: "12px 14px",
          background: "#f4f4f0", borderRadius: 10,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 700, color: "#655d54" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>add_location_alt</span>
              {t("myplace.add_to_my_places")}
            </p>
            <button
              onClick={() => {
                const allChecked = trip.placeKeys.every((k) => saveChecks[k]);
                const next = {};
                for (const k of trip.placeKeys) next[k] = !allChecked;
                setSaveChecks(next);
              }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontFamily: FL, fontSize: 11, color: "#8a8e8a", padding: 0,
              }}
            >
              {trip.placeKeys.every((k) => saveChecks[k]) ? t("myplace.deselect_all") : t("myplace.select_all")}
            </button>
          </div>
          {trip.placeKeys.map((k) => {
            const g = placeGroups[k];
            if (!g?.place) return null;
            const alreadySaved = savedPlaceIds.has(g.baseKey);
            // Check if place already exists in personal places (by coordinate proximity)
            const THRESHOLD = 0.001;
            const existsInPlaces = !alreadySaved && g.place.lat != null && existingPlaces.some((p) =>
              p.lat != null && p.lng != null && Math.sqrt((p.lat - g.place.lat) ** 2 + (p.lng - g.place.lng) ** 2) < THRESHOLD
            );
            return (
              <label
                key={k}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 0", cursor: alreadySaved ? "default" : "pointer",
                  opacity: alreadySaved ? 0.5 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={alreadySaved || saveChecks[k] || false}
                  disabled={alreadySaved}
                  onChange={(e) => setSaveChecks((prev) => ({ ...prev, [k]: e.target.checked }))}
                  style={{ accentColor: "#655d54", width: 16, height: 16, flexShrink: 0 }}
                />
                <span style={{
                  fontFamily: FL, fontSize: 13, color: "#2f3430",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  flex: 1,
                }}>
                  {g.place.name || `${g.place.lat?.toFixed(3)}, ${g.place.lng?.toFixed(3)}`}
                </span>
                {alreadySaved ? (
                  <span style={{ fontFamily: FL, fontSize: 10, color: "#1D9E75", fontWeight: 600 }}>{t("myplace.saved")}</span>
                ) : existsInPlaces && (
                  <span style={{ fontFamily: FL, fontSize: 10, color: "#E8943A", fontWeight: 600 }}>+ {t("myplace.photos_label")}</span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {/* Save Trip */}
      {onViewTrip && trip.placeKeys.length > 0 && (
        <button
          onClick={() => {
            const stops = trip.placeKeys
              .map((k, i) => {
                const g = placeGroups[k];
                if (!g?.place || g.place.lat == null || g.place.lng == null) return null;
                const thumb = g.photos[0]?.blobUrl || null;
                return {
                  place_id: savedPlaceDbIds[g.baseKey] || null,
                  lat: g.place.lat,
                  lng: g.place.lng,
                  name: g.place.name || "",
                  time: g._earliestTime ? formatTime(g._earliestTime) : "",
                  visited_at: g._earliestTime ? formatTime(g._earliestTime) : null,
                  order: i,
                  thumbnailUrl: thumb,
                  photos: g.photos.map((p) => ({
                    blobUrl: p.blobUrl,
                    isExisting: p.isExisting || false,
                  })),
                };
              })
              .filter(Boolean);
            if (stops.length === 0) return;
            // Collect places to save to My Places
            if (!isEdit && onBulkSavePlaces) {
              const placesToSave = trip.placeKeys
                .filter((k) => saveChecks[k] && !savedPlaceIds.has(placeGroups[k]?.baseKey))
                .map((k) => placeGroups[k])
                .filter(Boolean);
              if (placesToSave.length > 0) onBulkSavePlaces(placesToSave);
            }
            onViewTrip({
              id: editTripId || undefined,
              name: tripName || (trip.date ? formatDate(trip.date) : "My Trip"),
              date: trip.dateKey !== "_nodate" ? trip.dateKey : null,
              is_public: isPublic,
              stops,
            });
          }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            width: "100%", marginTop: 14,
            padding: "12px",
            background: "#E8943A", color: "#fff",
            border: "none", borderRadius: 10,
            fontFamily: FL, fontSize: 14, fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(232,148,58,0.25)",
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{isEdit ? "play_arrow" : "save"}</span>
          {isEdit ? t("myplace.save_and_play") : t("myplace.save_trip")}
        </button>
      )}
    </div>
  );
}

function PlaceGroupCard({ group, placeId, isSaved, onSave, onRemove, onRemovePhoto, onAddPhotos, photoEntries, onSelectCandidate, onOverridePlace }) {
  const [expanded, setExpanded] = useState(group.type === "no_location" && !group.place?.name);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const addPhotoRef = useRef(null);
  const place = group.place;
  const photoCount = group.photos.length;
  const isGpsOnly = group.type === "gps_only";
  const isNoLocation = group.type === "no_location";
  const candidates = photoEntries[group.photoIndices[0]]?.candidates || [];

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const request = {
        textQuery: searchQuery.trim(),
        fields: ["displayName", "formattedAddress", "location", "id", "types"],
        maxResultCount: 5,
      };
      const { places } = await Place.searchByText(request);
      setSearchResults((places || []).map((p) => ({
        name: p.displayName,
        address: p.formattedAddress,
        lat: p.location.lat(),
        lng: p.location.lng(),
        google_place_id: p.id,
        category: mapGoogleCategory(p.types),
      })));
    } catch {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const selectPlace = (newPlace) => {
    // Update all photos in this group to point to the new place
    group.photoIndices.forEach((idx) => onSelectCandidate(idx, newPlace.google_place_id));
    // Also need to add this place as a candidate so grouping works
    onOverridePlace?.(group.photoIndices, newPlace);
    setExpanded(false);
    setSearchResults([]);
    setSearchQuery("");
  };

  const CandidateButton = ({ c, isSelected }) => (
    <button
      onClick={() => {
        if (c.google_place_id) {
          group.photoIndices.forEach((idx) => onSelectCandidate(idx, c.google_place_id));
        } else {
          selectPlace(c);
        }
        setExpanded(false);
      }}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 10px", borderRadius: 8,
        border: isSelected ? `1.5px solid ${C.primary}` : "1.5px solid transparent",
        background: isSelected ? `${C.primary}0a` : C.container,
        cursor: "pointer", textAlign: "left", width: "100%",
      }}
    >
      <span className="material-symbols-outlined" style={{
        fontSize: 14, color: isSelected ? C.primary : C.outline,
      }}>
        {isSelected ? "radio_button_checked" : "radio_button_unchecked"}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.onSurface,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{c.name}</p>
        <p style={{
          margin: 0, fontFamily: FL, fontSize: 10, color: C.outline,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{c.address}</p>
      </div>
    </button>
  );

  return (
    <div style={{
      borderRadius: 12,
      background: isSaved ? "#E0F4EC" : "#fff",
      border: `1px solid ${isSaved ? "#1D9E7530" : "#e8e8e4"}`,
    }}>
      {/* Photo thumbnails strip — horizontally scrollable */}
      <div style={{
        display: "flex", gap: 3, padding: 3,
        overflowX: "auto", scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        borderRadius: "12px 12px 0 0",
      }}>
        {group.photos.map((p, i) => {
          const thumbSize = window.innerWidth <= 768 ? 100 : 80;
          return (
            <div key={i} style={{ position: "relative", flexShrink: 0 }}>
              <img
                src={p.blobUrl} alt=""
                style={{
                  width: thumbSize, height: thumbSize,
                  objectFit: "cover", display: "block",
                  borderRadius: i === 0 ? "10px 0 0 0" : i === photoCount - 1 ? "0 10px 0 0" : 0,
                }}
              />
              {/* Per-photo remove */}
              <button
                onClick={(e) => { e.stopPropagation(); onRemovePhoto(group.photoIndices[i]); }}
                style={{
                  position: "absolute", top: 4, right: 4,
                  width: 24, height: 24, borderRadius: "50%",
                  background: "rgba(0,0,0,0.5)", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#fff" }}>close</span>
              </button>
            </div>
          );
        })}
        {/* Add photos button */}
        <div
          onClick={() => addPhotoRef.current?.click()}
          style={{
            width: window.innerWidth <= 768 ? 100 : 80,
            height: window.innerWidth <= 768 ? 100 : 80,
            flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: C.container, cursor: "pointer",
            borderRadius: photoCount === 0 ? "10px 10px 0 0" : "0 10px 0 0",
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 28, color: C.outline }}>add_photo_alternate</span>
        </div>
        <input
          ref={addPhotoRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => { onAddPhotos(group, Array.from(e.target.files || [])); e.target.value = ""; }}
          style={{ display: "none" }}
        />
      </div>

      {/* Place info — tap to expand */}
      <div
        onClick={() => !isSaved && setExpanded(!expanded)}
        style={{ padding: "10px 14px", cursor: isSaved ? "default" : "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isSaved ? (
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#1D9E75" }}>check_circle</span>
          ) : isNoLocation ? (
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#E8943A" }}>help_outline</span>
          ) : isGpsOnly ? (
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.outline }}>location_on</span>
          ) : (
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.primary }}>place</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontFamily: FL, fontSize: 13, fontWeight: 700,
              color: isSaved ? "#1D9E75" : isNoLocation ? "#E8943A" : C.onSurface,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {isNoLocation ? (place?.name || "Tap to search place") :
               isGpsOnly ? `${place.lat.toFixed(4)}, ${place.lng.toFixed(4)}` :
               place?.name}
            </p>
            {place?.address && !isGpsOnly && !isNoLocation && (
              <p style={{
                margin: 0, fontFamily: FL, fontSize: 10, color: C.outline,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {place.address}
              </p>
            )}
          </div>
          {!isSaved && (
            <span className="material-symbols-outlined" style={{
              fontSize: 16, color: C.outline,
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}>
              expand_more
            </span>
          )}
        </div>
      </div>

      {/* Expanded: edit place + optional save */}
      {expanded && !isSaved && (
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ marginTop: 8 }}>
            {/* Existing candidates */}
            {candidates.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                {candidates.map((c) => (
                  <CandidateButton key={c.google_place_id} c={c} isSelected={c.google_place_id === placeId} />
                ))}
              </div>
            )}

            {/* Use coordinates only */}
            {!isGpsOnly && !isNoLocation && (
              <button
                onClick={() => {
                  // Switch to GPS-only mode for this group
                  group.photoIndices.forEach((idx) => onSelectCandidate(idx, null));
                  setExpanded(false);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", borderRadius: 8,
                  border: "1.5px solid transparent",
                  background: "#FFF8F0",
                  cursor: "pointer", textAlign: "left", width: "100%",
                  marginBottom: 8,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#E8943A" }}>
                  my_location
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.onSurface }}>
                    Use coordinates only
                  </p>
                  <p style={{ margin: 0, fontFamily: FL, fontSize: 10, color: C.outline }}>
                    Don't match to a specific place
                  </p>
                </div>
              </button>
            )}

            {/* Search bar */}
            <div style={{
              display: "flex", gap: 6, marginBottom: searchResults.length > 0 ? 8 : 0,
            }}>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search for a place..."
                style={{
                  flex: 1, padding: "8px 10px",
                  background: C.container, border: "none", borderRadius: 6,
                  fontFamily: FL, fontSize: 12, color: C.onSurface,
                  outline: "none", boxSizing: "border-box",
                }}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                style={{
                  padding: "8px 12px",
                  background: C.primary, color: "#fff6ef",
                  border: "none", borderRadius: 6,
                  fontFamily: FL, fontSize: 11, fontWeight: 700,
                  cursor: "pointer", flexShrink: 0,
                }}
              >
                {searching ? "..." : "Search"}
              </button>
            </div>

            {/* Search results */}
            {searchResults.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {searchResults.map((r) => (
                  <button
                    key={r.google_place_id}
                    onClick={() => selectPlace(r)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 8,
                      border: "1.5px solid transparent",
                      background: C.container,
                      cursor: "pointer", textAlign: "left", width: "100%",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outline }}>
                      search
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.onSurface,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{r.name}</p>
                      <p style={{
                        margin: 0, fontFamily: FL, fontSize: 10, color: C.outline,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{r.address}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Add to My Places — optional, inside expanded */}
            {!isGpsOnly && !isNoLocation && place?.name && (
              <button
                onClick={(e) => { e.stopPropagation(); onSave(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  marginTop: 10, padding: "6px 10px",
                  background: "none",
                  border: `1px solid #1D9E7540`,
                  borderRadius: 6,
                  fontFamily: FL, fontSize: 11, fontWeight: 600,
                  color: "#1D9E75",
                  cursor: "pointer",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>bookmark_add</span>
                Add to My Places
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
